import {
    IAgentRuntime,
    Memory,
    Provider,
    State,
    elizaLogger,
} from "@elizaos/core";
import { ethers } from "ethers";
import { Wallet, getDefaultProvider, CotiNetwork } from "@coti-io/coti-ethers";
import { CotiWalletConfig } from "../types/index";

export class CotiWalletProvider {
    private wallet: Wallet;
    private provider: any;
    private runtime: IAgentRuntime;
    private aesKey: string;

    constructor(runtime: IAgentRuntime, config: CotiWalletConfig) {
        this.runtime = runtime;

        // Initialize COTI provider
        this.provider = getDefaultProvider(CotiNetwork.Testnet);

        // Initialize wallet
        const privateKey = config.privateKey || process.env.COTI_PRIVATE_KEY;
        if (!privateKey) {
            throw new Error("COTI private key not provided");
        }

        // Initialize AES key for encryption
        this.aesKey = config.aesKey || process.env.COTI_AES_KEY || "";
        if (!this.aesKey) {
            elizaLogger.warn("COTI AES key not provided. Private token operations may not work properly.");
        }

        // Create COTI wallet and set AES key
        this.wallet = new Wallet(privateKey, this.provider);
        if (this.aesKey) {
            this.wallet.setAesKey(this.aesKey);
        }

        elizaLogger.info(`COTI Wallet initialized with address: ${this.wallet.address}`);
    }

    getAddress(): string {
        return this.wallet.address;
    }

    getAesKey(): string {
        return this.aesKey;
    }

    async getBalance(address?: string): Promise<bigint> {
        const targetAddress = address || this.wallet.address;
        return await this.provider.getBalance(targetAddress);
    }

    async transfer(to: string, amount: string): Promise<ethers.TransactionResponse> {
        const amountWei = ethers.parseEther(amount);
        
        elizaLogger.info(`Transferring ${amount} COTI to ${to}`);
        
        const tx = await this.wallet.sendTransaction({
            to,
            value: amountWei,
        });

        elizaLogger.info(`Transaction sent: ${tx.hash}`);
        return tx;
    }

    async getTransactionReceipt(txHash: string): Promise<ethers.TransactionReceipt | null> {
        return await this.provider.getTransactionReceipt(txHash);
    }

    async estimateGas(to: string, amount: string): Promise<bigint> {
        const amountWei = ethers.parseEther(amount);
        
        return await this.wallet.estimateGas({
            to,
            value: amountWei,
        });
    }

    getProvider(): ethers.JsonRpcProvider {
        return this.provider;
    }

    getWallet(): ethers.Wallet {
        return this.wallet;
    }
}

// Global wallet provider instance
let walletProviderInstance: CotiWalletProvider | null = null;

export function initCotiWalletProvider(runtime: IAgentRuntime): CotiWalletProvider {
    if (!walletProviderInstance) {
        const config: CotiWalletConfig = {
            privateKey: process.env.COTI_PRIVATE_KEY,
            rpcUrl: process.env.COTI_RPC_URL,
            networkId: parseInt(process.env.COTI_NETWORK_ID || "7701"),
        };
        
        walletProviderInstance = new CotiWalletProvider(runtime, config);
    }
    
    return walletProviderInstance;
}

export const cotiWalletProvider: Provider = {
    name: "COTI_WALLET_PROVIDER",
    description: "Provides COTI wallet functionality for blockchain operations",

    get: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined
    ) => {
        try {
            const walletProvider = initCotiWalletProvider(runtime);
            const address = walletProvider.getAddress();
            const balance = await walletProvider.getBalance();

            return {
                text: `COTI Wallet Address: ${address}, Balance: ${ethers.formatEther(balance)} COTI`,
                values: {
                    address,
                    balance: ethers.formatEther(balance),
                    balanceWei: balance.toString(),
                },
                data: {
                    address,
                    balance: ethers.formatEther(balance),
                    balanceWei: balance.toString(),
                },
            };
        } catch (error) {
            elizaLogger.error("Error in COTI wallet provider:", error);
            return {
                text: `Error accessing COTI wallet: ${error instanceof Error ? error.message : String(error)}`,
                values: {},
                data: { error: error instanceof Error ? error.message : String(error) },
            };
        }
    },
};
